<script lang="ts" setup>
import { computed, onMounted, ref, Ref } from 'vue';
import Chip from 'primevue/chip';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import RemoveCaseManagerModal from './RemoveCaseManagerModal.vue';
import { useArchiveStore, useCaseManagerStore } from '@/store';
import { CaseManagerModal, CaseManagerTable } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const caseManagerStore = useCaseManagerStore();
const archiveStore = useArchiveStore();
const caseManagers: Ref<CaseManagerTable[]> = ref([]);
const caseManagerCount: Ref<number> = computed(() => (caseManagers.value ? caseManagers.value.length : 0));
const caseManagerModalOpen: Ref<boolean> = ref(false);
const archivedVisible = ref(false);
const caseManagerItems: Ref<CaseManagerModal[]> = ref([]);
const caseManagerToRemove: Ref<CaseManagerTable | null> = ref(null);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_CASE_MANAGER_INDEX])) getCaseManagers(false);
});

const getCaseManagers: (showArchived: boolean) => Promise<void> = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  await caseManagerStore
    .getCaseManagersForTable(filterArchived)
    .then((result) => (caseManagers.value = result))
    .catch((e) => {
      caseManagers.value = [];
      throw e;
    });
};

const createMenuModel = (item: CaseManagerTable) => {
  const menuItems = [
    {
      label: 'Remove Case Manager',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_CASE_MANAGERS]),
      command: () => {
        caseManagerToRemove.value = item;
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachCaseManagers: (ids: number[]) => Promise<void> = async (ids) => {
  await caseManagerStore.attachCaseManagers(ids);
  getCaseManagers(false);
  caseManagerStore.doToast('Case Manager(s) has been added', 'Added');
};

const detachCaseManager: (ids: number[], caseManagerName: string) => Promise<void> = async (ids, caseManagerName) => {
  await caseManagerStore.detachCaseManagers(ids);
  getCaseManagers(false);
  caseManagerStore.doToast(`${caseManagerName} has been removed from organization`, 'Removed');
};

const onConfirmRemove = () => {
  const item = caseManagerToRemove.value as CaseManagerTable;
  caseManagerToRemove.value = null;
  detachCaseManager([item.id], item.name);
};

const onCancelRemove = () => (caseManagerToRemove.value = null);

const openAddCaseManagerModal = async () => {
  caseManagerModalOpen.value = true;
  caseManagerItems.value = await caseManagerStore.getCaseManagersAvailableToAdd();
};

const onCancelAdd = () => (caseManagerModalOpen.value = false);

const onSaveAdd = (caseManagers: CaseManagerModal[]) => {
  let caseManagerIds = caseManagers.map((caseManager) => {
    return caseManager.value;
  });
  attachCaseManagers(caseManagerIds);
  caseManagerModalOpen.value = false;
};
const showArchived = () => {
  archivedVisible.value = true;
  caseManagerStore.showArchived = true;
  getCaseManagers(true);
};
const hideArchived = () => {
  archivedVisible.value = false;
  caseManagerStore.showArchived = false;
  getCaseManagers(false);
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Case Managers</h2>
      <Chip>{{ caseManagerCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATION_CASE_MANAGER_SHOW_ARCHIVED])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_CASE_MANAGERS])"
          type="text"
          size="small"
          text="Add Case Manager"
          icon-left="add"
          @click="openAddCaseManagerModal"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="caseManagers"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :row-class="({ archived }) => (archived ? 'grey-row' : null)"
        :loading="caseManagerStore.loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-case-manager-table"
        state-storage="session"
        :class="{ 'table-loading': caseManagerStore.loading }"
      >
        <template v-if="caseManagerCount">
          <Column field="name" header="Case Manager Name" header-style="color: #3B4242" sortable>
            <template #body="{ data }">
              <Icon
                v-if="data.archived"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_INDEX])"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.name" />
            </template>
          </Column>
          <Column
            body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </template>
        <template #empty>Case Managers not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="caseManagerModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_CASE_MANAGERS])"
        title="Add case managers"
        :items="caseManagerItems"
        :searchable="true"
        button-text="Add case managers"
        placeholder="Search case managers"
        person-type="Case manager"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
      <RemoveCaseManagerModal
        v-if="caseManagerToRemove"
        :case-manager-name="caseManagerToRemove.name"
        @accept="onConfirmRemove"
        @cancel="onCancelRemove"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
